<template>
  <div class="admin-page">
    <div class="admin-page-inner">
      <div class="admin-page-header">
        <div>
          <span class="admin-kicker">Auswertung &amp; Abrechnung</span>
          <h1 class="admin-page-title">Abrechnung</h1>
        </div>
      </div>

      <div>
        <!-- Tab Navigation -->
        <div class="admin-tabs">
          <button type="button" class="admin-tab" :class="{ 'admin-tab--active': activeTab === 'create' }"
            @click="activeTab = 'create'">
            Rechnung erstellen
          </button>
          <button type="button" class="admin-tab" :class="{ 'admin-tab--active': activeTab === 'list' }"
            @click="activeTab = 'list'">
            Rechnungen ansehen
          </button>
        </div>

        <!-- Create Invoice Tab -->
        <div v-if="activeTab === 'create'" class="space-y-6">
          <!-- Contract and Date Selection -->
          <div class="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
            <h3 class="text-2xl font-bold text-gray-800 mb-4">Vertrag und Zeitraum auswählen</h3>
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label class="block text-sm font-semibold text-gray-700 mb-2">Vertrag</label>
                <select v-model="selectedContract" @change="loadJobs"
                  class="w-full border-2 border-gray-300 rounded-xl px-4 py-3 focus:border-caspyellow focus:ring-2 focus:ring-caspyellow focus:ring-opacity-20 focus:outline-none transition-all duration-200">
                  <option :value="null">Bitte wählen...</option>
                  <option v-for="contract in contracts" :key="contract.id" :value="contract.id">
                    {{ contract.vertragsbeschreibung }}
                  </option>
                </select>
              </div>
              <div>
                <label class="block text-sm font-semibold text-gray-700 mb-2">Von Datum</label>
                <input type="date" v-model="startDate" @change="loadJobs"
                  class="w-full border-2 border-gray-300 rounded-xl px-4 py-3 focus:border-caspyellow focus:ring-2 focus:ring-caspyellow focus:ring-opacity-20 focus:outline-none transition-all duration-200" />
              </div>
              <div>
                <label class="block text-sm font-semibold text-gray-700 mb-2">Bis Datum</label>
                <input type="date" v-model="endDate" @change="loadJobs"
                  class="w-full border-2 border-gray-300 rounded-xl px-4 py-3 focus:border-caspyellow focus:ring-2 focus:ring-caspyellow focus:ring-opacity-20 focus:outline-none transition-all duration-200" />
              </div>
            </div>
            <div class="flex items-center mt-4">
              <input type="checkbox" v-model="skipBilled" class="mr-2" id="onlyUnbilled" @change="loadJobs">
              <label for="onlyUnbilled" class="text-sm font-semibold text-gray-700">Nur unbezahlte Aufträge anzeigen</label>
            </div>
          </div>

          <!-- Jobs Table -->
          <div v-if="jobs.length > 0"
            class="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-200">
            <div class="px-6 py-4" style="background-color: #5f656e;">
              <div class="flex justify-between items-center">
                <h3 class="text-2xl font-bold text-white">Aufträge ({{ jobs.length }})</h3>
                <div class="text-white text-sm">
                  {{ selectedJobs.length }} ausgewählt
                </div>
              </div>
            </div>
            <div class="px-6 py-4 bg-gray-50 border-b border-gray-200">
              <div class="flex flex-wrap justify-between items-center gap-3 mb-3">
                <div class="flex gap-3">
                  <button type="button" @click="selectAllJobs"
                    class="admin-button--secondary admin-button--sm">
                    Alle auswählen
                  </button>
                  <button type="button" @click="deselectAllJobs"
                    class="admin-button--secondary admin-button--sm">
                    Alle abwählen
                  </button>
                </div>
                <button type="button" @click="generatePreview" :disabled="selectedJobs.length === 0"
                  class="admin-button--secondary">
                  Vorschau generieren
                </button>
              </div>
              
              <!-- Pagination Controls (Top) -->
              <div v-if="totalPages > 1" class="flex justify-center items-center gap-2 mb-3 pb-3 border-b border-gray-300">
                <button type="button" @click="currentPage = 1" :disabled="currentPage === 1"
                  class="admin-tab">
                  ««
                </button>
                <button type="button" @click="currentPage--" :disabled="currentPage === 1"
                  class="admin-tab">
                  «
                </button>
                <span class="px-4 py-1 text-sm font-semibold text-gray-700">
                  Seite {{ currentPage }} von {{ totalPages }}
                </span>
                <button type="button" @click="currentPage++" :disabled="currentPage === totalPages"
                  class="admin-tab">
                  »
                </button>
                <button type="button" @click="currentPage = totalPages" :disabled="currentPage === totalPages"
                  class="admin-tab">
                  »»
                </button>
              </div>
              
              <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label class="block text-xs font-semibold text-gray-700 mb-1">Filter: Einsatzort</label>
                  <select v-model="filterEinsatzort"
                    class="w-full border-2 border-gray-300 rounded-lg px-3 py-2 text-sm focus:border-caspyellow focus:ring-2 focus:ring-caspyellow focus:ring-opacity-20 focus:outline-none transition-all duration-200">
                    <option value="">Alle Einsatzorte</option>
                    <option v-for="ort in uniqueEinsatzorte" :key="ort" :value="ort">
                      {{ ort }}
                    </option>
                  </select>
                </div>
                <div>
                  <label class="block text-xs font-semibold text-gray-700 mb-1">Filter: Einsatzstelle</label>
                  <select v-model="filterEinsatzstelle"
                    class="w-full border-2 border-gray-300 rounded-lg px-3 py-2 text-sm focus:border-caspyellow focus:ring-2 focus:ring-caspyellow focus:ring-opacity-20 focus:outline-none transition-all duration-200">
                    <option value="">Alle Einsatzstellen</option>
                    <option v-for="stelle in uniqueEinsatzstellen" :key="stelle" :value="stelle">
                      {{ stelle }}
                    </option>
                  </select>
                </div>
              </div>
            </div>
            <div class="px-6 py-2 bg-gray-50 border-b border-gray-200 flex justify-between items-center gap-3">
              <div class="text-sm text-gray-600">
                Zeige {{ ((currentPage - 1) * itemsPerPage) + 1 }}-{{ Math.min(currentPage * itemsPerPage, filteredJobs.length) }} von {{ filteredJobs.length }} Aufträgen ({{ jobs.length }} gesamt)
              </div>
              <button type="button" @click="generatePreview" :disabled="selectedJobs.length === 0"
                class="admin-button--secondary">
                Vorschau generieren
              </button>
            </div>
            <div class="overflow-x-auto">
              <table class="w-full">
                <thead class="bg-gray-100 border-b-2 border-gray-300">
                  <tr>
                    <th class="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                      <input type="checkbox" @change="toggleAllJobs"
                        :checked="paginatedJobs.length > 0 && paginatedJobs.every(job => selectedJobs.includes(job.id))"
                        :indeterminate.prop="paginatedJobs.some(job => selectedJobs.includes(job.id)) && !paginatedJobs.every(job => selectedJobs.includes(job.id))"
                        class="w-4 h-4 rounded border-gray-300 text-caspyellow focus:ring-caspyellow" />
                    </th>
                    <th class="px-4 py-3 text-left text-sm font-semibold text-gray-700">Einsatzort</th>
                    <th class="px-4 py-3 text-left text-sm font-semibold text-gray-700">Einsatzstelle</th>
                    <th class="px-4 py-3 text-left text-sm font-semibold text-gray-700">Mitarbeiter</th>
                    <th class="px-4 py-3 text-left text-sm font-semibold text-gray-700">Tag</th>
                    <th class="px-4 py-3 text-left text-sm font-semibold text-gray-700">Von</th>
                    <th class="px-4 py-3 text-left text-sm font-semibold text-gray-700">Bis</th>
                    <th class="px-4 py-3 text-center text-sm font-semibold text-gray-700">Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="job in paginatedJobs" :key="job.id" @click="toggleJob(job.id, $event)"
                    @mousedown="preventTextSelection"
                    class="border-b border-gray-200 cursor-pointer transition-colors duration-150" :class="{
                      'selected-row': selectedJobs.includes(job.id),
                      'bg-gray-100 hover:bg-gray-200': !selectedJobs.includes(job.id) && job.abgerechnet,
                      'hover:bg-gray-50': !selectedJobs.includes(job.id) && !job.abgerechnet
                    }" style="user-select: none;">
                    <td class="px-4 py-3">
                      <input type="checkbox" :checked="selectedJobs.includes(job.id)" @click.stop
                        @change="toggleJob(job.id, $event)"
                        class="w-4 h-4 rounded border-gray-300 text-caspyellow focus:ring-caspyellow" />
                    </td>
                    <td class="px-4 py-3 text-sm">{{ job.Einsatzort_Beschreibung }}</td>
                    <td class="px-4 py-3 text-sm">{{ job.Einsatzstelle_Beschreibung }}</td>
                    <td class="px-4 py-3 text-sm">{{ job.Mitarbeitername }}</td>
                    <td class="px-4 py-3 text-sm">{{ formatDate(job.Datum_von) }}</td>
                    <td class="px-4 py-3 text-sm">{{ job.Von_Uhrzeit || '-' }}</td>
                    <td class="px-4 py-3 text-sm">{{ job.Bis_Uhrzeit || '-' }}</td>
                    <td class="px-4 py-3 text-center">
                      <span v-if="job.bezahlt"
                        class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Bezahlt
                      </span>
                      <span v-else-if="job.abgerechnet"
                        class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        Abgerechnet
                      </span>
                      <span v-else
                        class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        Offen
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            <!-- Pagination Controls -->
            <div v-if="totalPages > 1" class="px-6 py-3 bg-gray-50 border-t border-gray-200 flex justify-center items-center gap-2">
              <button type="button" @click="currentPage = 1" :disabled="currentPage === 1"
                class="admin-tab">
                ««
              </button>
              <button type="button" @click="currentPage--" :disabled="currentPage === 1"
                class="admin-tab">
                «
              </button>
              <span class="px-4 py-1 text-sm font-semibold text-gray-700">
                Seite {{ currentPage }} von {{ totalPages }}
              </span>
              <button type="button" @click="currentPage++" :disabled="currentPage === totalPages"
                class="admin-tab">
                »
              </button>
              <button type="button" @click="currentPage = totalPages" :disabled="currentPage === totalPages"
                class="admin-tab">
                »»
              </button>
            </div>
            
            <div class="px-6 py-4 bg-gray-50 flex justify-between items-center gap-3">
              <div class="flex gap-3">
                <button type="button" @click="selectAllJobs"
                  class="admin-button--secondary admin-button--sm">
                  Alle auswählen
                </button>
                <button type="button" @click="deselectAllJobs"
                  class="admin-button--secondary admin-button--sm">
                  Alle abwählen
                </button>
              </div>
              <button type="button" @click="generatePreview" :disabled="selectedJobs.length === 0"
                class="admin-button--secondary">
                Vorschau generieren
              </button>
            </div>
          </div>

          <div v-else-if="selectedContract && jobs.length === 0"
            class="bg-white rounded-2xl shadow-lg p-8 text-center border border-gray-200">
            <p class="text-gray-500">Keine Aufträge für den ausgewählten Zeitraum gefunden.</p>
          </div>

          <!-- Invoice Preview Modal -->
          <div v-if="showPreviewModal"
            class="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
            @click.self="closePreviewModal">
            <div class="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto" @click.stop>
              <div
                class="sticky top-0 bg-white z-10 px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                <h2 class="text-2xl font-bold text-gray-800">Rechnungsvorschau</h2>
                <button type="button" @click="closePreviewModal" class="admin-button--ghost admin-button--icon" aria-label="Vorschau schließen">
                  <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <!-- Preview Content -->
              <div v-if="invoicePreview && !creatingInvoice && !generatingPDF" class="p-6">
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <h4 class="font-semibold text-gray-700 mb-2">Vertrag</h4>
                    <p class="text-gray-600">{{ invoicePreview.contract.vertragsbeschreibung }}</p>
                  </div>
                  <div>
                    <h4 class="font-semibold text-gray-700 mb-2">Debitor</h4>
                    <p class="text-gray-600">
                      {{ invoicePreview.debitor.name }}
                      <span v-if="invoicePreview.debitor.unternehmensname" class="text-gray-500">
                        ({{ invoicePreview.debitor.unternehmensname }})
                      </span>
                    </p>
                    <p class="text-sm text-gray-500">Kundennummer: APD-{{ invoicePreview.debitor.id }}</p>
                  </div>
                  <div>
                    <h4 class="font-semibold text-gray-700 mb-2">Leistungszeitraum</h4>
                    <p class="text-gray-600">
                      {{ formatDate(invoicePreview.service_period_start) }} - {{
                        formatDate(invoicePreview.service_period_end) }}
                    </p>
                  </div>
                  <div>
                    <h4 class="font-semibold text-gray-700 mb-2">Anzahl Aufträge</h4>
                    <p class="text-gray-600">{{ invoicePreview.job_count }}</p>
                  </div>
                </div>

                <!-- Warnings Section -->
                <div v-if="invoicePreview.warnings && invoicePreview.warnings.length > 0"
                  class="mb-6 p-4 bg-yellow-50 border-2 border-yellow-400 rounded-lg">
                  <div class="flex items-start gap-3">
                    <svg class="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor"
                      viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <div class="flex-1">
                      <h4 class="font-bold text-yellow-900 mb-2">Warnung: Fehlende Verrechnungstarife</h4>
                      <div v-for="warning in invoicePreview.warnings" :key="warning.rubrikencode" class="mb-3">
                        <div v-if="warning.type === 'missing_verrechnungstarif'" class="text-sm">
                          <p class="font-semibold text-yellow-900 mb-1">
                            Rubrikencode "{{ warning.rubrikencode }}" hat keinen zugeordneten Verrechnungstarif-Preis
                          </p>
                          <p class="text-yellow-800 mb-2">
                            {{ warning.count }} {{ warning.count === 1 ? 'Auftrag wurde' : 'Aufträge wurden' }}
                            übersprungen und werden nicht abgerechnet.
                          </p>
                          <details class="text-xs text-yellow-800">
                            <summary class="cursor-pointer hover:text-yellow-900 font-medium">Betroffene Aufträge
                              anzeigen</summary>
                            <ul class="mt-2 ml-4 space-y-1">
                              <li v-for="job in warning.jobs" :key="job.jobId" class="list-disc">
                                {{ formatDate(job.date) }} - {{ job.einsatzort }} / {{ job.einsatzstelle }} - {{
                                job.mitarbeiter }}
                              </li>
                            </ul>
                          </details>
                        </div>
                        <div v-else-if="warning.type === 'missing_all_tariffs'" class="text-sm">
                          <p class="font-semibold text-yellow-900">
                            {{ warning.count }} {{ warning.count === 1 ? 'Auftrag hat' : 'Aufträge haben' }} weder
                            Verrechnungs- noch Basistarif
                          </p>
                        </div>
                      </div>
                      <p class="text-sm text-yellow-900 font-medium mt-3">
                        Bitte fügen Sie die fehlenden Verrechnungstarife hinzu oder entfernen Sie diese Aufträge aus der
                        Auswahl.
                      </p>
                    </div>
                  </div>
                </div>

                <!-- Applied Tariffs Section -->
                <div v-if="invoicePreview.applied_tariffs"
                  class="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h4 class="font-semibold text-gray-700 mb-3">Angewendete Tarife</h4>
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h5 class="text-sm font-semibold text-gray-600 mb-2">Verrechnungstarife</h5>
                      <div v-if="invoicePreview.applied_tariffs.verrechnungstarife.length > 0">
                        <div v-for="vt in invoicePreview.applied_tariffs.verrechnungstarife" :key="vt.id"
                          class="text-sm text-gray-700 mb-1">
                          <span class="font-medium">{{ vt.name }}</span>
                          <span v-if="vt.laufzeitVon && vt.laufzeitBis" class="text-gray-500 text-xs ml-2">
                            ({{ formatDate(vt.laufzeitVon) }} - {{ formatDate(vt.laufzeitBis) }})
                          </span>
                        </div>
                      </div>
                      <p v-else class="text-sm text-gray-500 italic">Keine Verrechnungstarife zugeordnet</p>
                    </div>
                    <div>
                      <h5 class="text-sm font-semibold text-gray-600 mb-2">Basistarife</h5>
                      <div v-if="invoicePreview.applied_tariffs.basistarife.length > 0">
                        <div v-for="bt in invoicePreview.applied_tariffs.basistarife" :key="bt.id"
                          class="text-sm text-gray-700 mb-1">
                          <span class="font-medium">{{ bt.name }}</span>
                          <span v-if="bt.laufzeitVon && bt.laufzeitBis" class="text-gray-500 text-xs ml-2">
                            ({{ formatDate(bt.laufzeitVon) }} - {{ formatDate(bt.laufzeitBis) }})
                          </span>
                        </div>
                      </div>
                      <p v-else class="text-sm text-gray-500 italic">Keine Basistarife verfügbar</p>
                    </div>
                  </div>
                </div>

                <h4 class="font-semibold text-gray-700 mb-3">Rechnungspositionen</h4>
                <div class="overflow-x-auto mb-6">
                  <table class="w-full border border-gray-200 rounded-lg overflow-hidden">
                    <thead class="bg-gray-100">
                      <tr>
                        <th class="px-4 py-3 text-left text-sm font-semibold text-gray-700">Position</th>
                        <th class="px-4 py-3 text-right text-sm font-semibold text-gray-700">Stunden</th>
                        <th class="px-4 py-3 text-right text-sm font-semibold text-gray-700">Preis/Std.</th>
                        <th class="px-4 py-3 text-right text-sm font-semibold text-gray-700">Betrag</th>
                      </tr>
                    </thead>
                    <tbody>
                      <template v-for="(item, index) in invoicePreview.line_items" :key="index">
                        <tr v-if="isNewVertragGroup(index)" class="border-b-2 border-gray-300 bg-gray-200">
                          <td colspan="4" class="px-4 py-2 text-sm font-bold text-gray-800 uppercase">
                            {{ item.vertrag || 'Ohne Vertrag' }}
                          </td>
                        </tr>
                        <tr v-if="isNewEinsatzstelleGroup(index)" class="border-b border-gray-200 bg-gray-50">
                          <td colspan="4" class="px-4 py-2 text-sm font-semibold text-gray-700">
                            {{ item.einsatzstelle || 'Ohne Einsatzstelle' }}
                          </td>
                        </tr>
                        <tr class="border-b border-gray-200">
                          <td class="px-4 py-3 text-sm" :class="item.isSurcharge ? 'pl-12' : 'pl-8'">
                            {{ item.rubrikencode }}
                          </td>
                          <td class="px-4 py-3 text-sm text-right">
                            {{ item.hours.toFixed(2) }} h
                          </td>
                          <td class="px-4 py-3 text-sm text-right">
                            {{ formatCurrency(item.rate) }}
                          </td>
                          <td class="px-4 py-3 text-sm text-right font-semibold">
                            {{ formatCurrency(item.amount) }}
                          </td>
                        </tr>
                      </template>
                    </tbody>
                  </table>
                </div>

                <div class="flex justify-end">
                  <div class="w-full md:w-1/2 space-y-2">
                    <div class="flex justify-between text-gray-700">
                      <span>Nettobetrag:</span>
                      <span class="font-semibold">{{ formatCurrency(invoicePreview.total_net) }}</span>
                    </div>
                    <div class="flex justify-between text-gray-700">
                      <span>MwSt. {{ invoicePreview.tax_rate }}%:</span>
                      <span class="font-semibold">{{ formatCurrency(invoicePreview.total_tax) }}</span>
                    </div>
                    <div class="flex justify-between text-lg font-bold text-gray-900 border-t-2 border-gray-300 pt-2">
                      <span>Gesamtbetrag:</span>
                      <span>{{ formatCurrency(invoicePreview.total_gross) }}</span>
                    </div>
                  </div>
                </div>

                <div class="flex justify-end gap-3 mt-6 px-6 pb-6">
                  <button type="button" @click="closePreviewModal"
                    class="admin-button--secondary">
                    Abbrechen
                  </button>
                  <button type="button" @click="billJobsWithoutPDF"
                    class="admin-button--secondary">
                    Aufträge als abgerechnet markieren
                  </button>
                  <button type="button" @click="createInvoiceWithPDF"
                    class="admin-button--primary">
                    Rechnung erstellen
                  </button>
                </div>
              </div>

              <!-- Loading States -->
              <div v-if="creatingInvoice || generatingPDF" class="p-8">
                <div class="space-y-6">
                  <!-- Step 1: Creating Invoice -->
                  <div class="flex items-center gap-4">
                    <div v-if="creatingInvoice" class="flex-shrink-0">
                      <div class="animate-spin rounded-full h-8 w-8 border-4 border-blue-600 border-t-transparent">
                      </div>
                    </div>
                    <div v-else class="flex-shrink-0">
                      <svg class="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div class="flex-1">
                      <h3 class="text-lg font-semibold text-gray-800">Rechnung wird erstellt...</h3>
                      <p class="text-sm text-gray-600">Rechnungsdaten werden gespeichert</p>
                    </div>
                  </div>

                  <!-- Step 2: Generating PDF -->
                  <div class="flex items-center gap-4" :class="{ 'opacity-50': creatingInvoice }">
                    <div v-if="generatingPDF" class="flex-shrink-0">
                      <div class="animate-spin rounded-full h-8 w-8 border-4 border-blue-600 border-t-transparent">
                      </div>
                    </div>
                    <div v-else-if="!creatingInvoice && !generatingPDF && createdInvoiceId" class="flex-shrink-0">
                      <svg class="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div v-else class="flex-shrink-0">
                      <div class="w-8 h-8 rounded-full border-4 border-gray-300"></div>
                    </div>
                    <div class="flex-1">
                      <h3 class="text-lg font-semibold text-gray-800">PDF wird generiert...</h3>
                      <p class="text-sm text-gray-600">Rechnungs-PDF wird erstellt</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Invoices List Tab -->
        <div v-if="activeTab === 'list'" class="space-y-6">
          <div v-if="loadingInvoices" class="flex items-center justify-center py-20">
            <div class="text-center">
              <div
                class="inline-block animate-spin rounded-full h-12 w-12 border-4 border-gray-300 border-t-caspyellow mb-4">
              </div>
              <p class="text-gray-600 font-medium">Lade Rechnungen...</p>
            </div>
          </div>

          <div v-else-if="invoices.length === 0"
            class="bg-white rounded-2xl shadow-lg p-8 text-center border border-gray-200">
            <p class="text-gray-500">Keine Rechnungen vorhanden.</p>
          </div>

          <div v-else class="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-200">
            <div class="px-6 py-4" style="background-color: #5f656e;">
              <h3 class="text-2xl font-bold text-white">Rechnungen ({{ invoices.length }})</h3>
            </div>
            <div class="overflow-x-auto">
              <table class="w-full">
                <thead class="bg-gray-100 border-b-2 border-gray-300">
                  <tr>
                    <th class="px-4 py-3 text-left text-sm font-semibold text-gray-700">Rechnungsnr.</th>
                    <th class="px-4 py-3 text-left text-sm font-semibold text-gray-700">Vertrag</th>
                    <th class="px-4 py-3 text-left text-sm font-semibold text-gray-700">Debitor</th>
                    <th class="px-4 py-3 text-left text-sm font-semibold text-gray-700">Datum</th>
                    <th class="px-4 py-3 text-left text-sm font-semibold text-gray-700">Zeitraum</th>
                    <th class="px-4 py-3 text-right text-sm font-semibold text-gray-700">Betrag</th>
                    <th class="px-4 py-3 text-center text-sm font-semibold text-gray-700">Status</th>
                    <th class="px-4 py-3 text-center text-sm font-semibold text-gray-700">Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="invoice in invoices" :key="invoice.id" class="border-b border-gray-200 hover:bg-gray-50">
                    <td class="px-4 py-3 text-sm font-mono font-semibold">{{ invoice.invoice_number }}</td>
                    <td class="px-4 py-3 text-sm">
                      <template v-if="invoice.vertraege && invoice.vertraege.length > 0">
                        <div v-for="v in invoice.vertraege" :key="v.name" class="mb-1 last:mb-0">
                          <div>{{ v.name || 'Ohne Vertrag' }}</div>
                          <div v-if="v.einsatzstellen.length > 0"
                            class="text-xs font-light text-gray-500 mt-0.5">
                            {{ v.einsatzstellen.join(', ') }}
                          </div>
                        </div>
                      </template>
                      <div v-else>{{ invoice.contract?.vertragsbeschreibung || '-' }}</div>
                    </td>
                    <td class="px-4 py-3 text-sm">
                      {{ invoice.contract?.debitor?.name || '-' }}
                      <span v-if="invoice.contract?.debitor?.unternehmensname" class="text-gray-500 text-xs">
                        ({{ invoice.contract.debitor.unternehmensname }})
                      </span>
                    </td>
                    <td class="px-4 py-3 text-sm">{{ formatDate(invoice.invoice_date) }}</td>
                    <td class="px-4 py-3 text-sm">
                      {{ formatDate(invoice.service_period_start) }} - {{ formatDate(invoice.service_period_end) }}
                    </td>
                    <td class="px-4 py-3 text-sm text-right font-semibold">{{ formatCurrency(invoice.total_gross) }}
                    </td>
                    <td class="px-4 py-3 text-center">
                      <select :value="invoice.status" @change="updateInvoiceStatus(invoice.id, $event.target.value)"
                        class="text-xs px-2 py-1 rounded-lg border-2 focus:outline-none focus:ring-2 focus:ring-caspyellow"
                        :class="{
                          'bg-gray-100 border-gray-300 text-gray-800': invoice.status === 'draft',
                          'bg-blue-100 border-blue-300 text-blue-800': invoice.status === 'sent',
                          'bg-green-100 border-green-300 text-green-800': invoice.status === 'paid'
                        }">
                        <option value="draft">Entwurf</option>
                        <option value="sent">Versendet</option>
                        <option value="paid">Bezahlt</option>
                      </select>
                    </td>
                    <td class="px-4 py-3 text-center">
                      <div class="flex justify-center gap-2">
                        <button v-if="!invoice.pdf_path" type="button" @click="generatePDF(invoice.id)"
                          class="admin-button--secondary admin-button--sm"
                          title="PDF generieren">
                          PDF
                        </button>
                        <button v-else type="button" @click="viewPDF(invoice.id)"
                          class="admin-button--secondary admin-button--sm"
                          title="PDF anzeigen">
                          <i class="fas fa-file-pdf"></i> PDF
                        </button>
                        <button type="button" @click="deleteInvoiceConfirm(invoice.id)"
                          class="admin-button--danger admin-button--sm"
                          title="Löschen" aria-label="Rechnung löschen">
                          <i class="fas fa-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Toast Notifications -->
    <div class="fixed top-4 right-4 z-50 space-y-2">
      <transition-group name="toast">
        <div v-for="toast in toasts" :key="toast.id" :class="[
          'px-6 py-4 rounded-xl shadow-2xl max-w-md flex items-start gap-3 border-2',
          toast.type === 'success' ? 'bg-green-50 border-green-500 text-green-900' : '',
          toast.type === 'error' ? 'bg-red-50 border-red-500 text-red-900' : '',
          toast.type === 'warning' ? 'bg-yellow-50 border-yellow-500 text-yellow-900' : '',
          toast.type === 'info' ? 'bg-blue-50 border-blue-500 text-blue-900' : ''
        ]">
          <div class="flex-shrink-0">
            <svg v-if="toast.type === 'success'" class="w-6 h-6 text-green-600" fill="none" stroke="currentColor"
              viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
            <svg v-else-if="toast.type === 'error'" class="w-6 h-6 text-red-600" fill="none" stroke="currentColor"
              viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
            <svg v-else-if="toast.type === 'warning'" class="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor"
              viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <svg v-else class="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div class="flex-1 font-medium">{{ toast.message }}</div>
          <button type="button" @click="removeToast(toast.id)"
            class="admin-button--ghost admin-button--icon flex-shrink-0 -mt-3 -mr-3"
            aria-label="Benachrichtigung schließen">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </transition-group>
    </div>
  </div>
</template>

<script>
import CASP from '../../CASP';

export default {
  data() {
    return {
      activeTab: 'create',
      contracts: [],
      selectedContract: null,
      startDate: '',
      endDate: '',
      jobs: [],
      selectedJobs: [],
      lastSelectedIndex: null,
      filterEinsatzort: '',
      filterEinsatzstelle: '',
      invoicePreview: null,
      showPreviewModal: false,
      creatingInvoice: false,
      generatingPDF: false,
      createdInvoiceId: null,
      invoices: [],
      loadingInvoices: false,
      toasts: [],
      toastIdCounter: 0,
      skipBilled: true,
      currentPage: 1,
      itemsPerPage: 50
    };
  },
  computed: {
    filteredJobs() {
      let filtered = this.jobs;

      if (this.filterEinsatzort) {
        filtered = filtered.filter(job =>
          job.Einsatzort_Beschreibung &&
          job.Einsatzort_Beschreibung.toLowerCase().includes(this.filterEinsatzort.toLowerCase())
        );
      }

      if (this.filterEinsatzstelle) {
        filtered = filtered.filter(job =>
          job.Einsatzstelle_Beschreibung &&
          job.Einsatzstelle_Beschreibung.toLowerCase().includes(this.filterEinsatzstelle.toLowerCase())
        );
      }

      return filtered;
    },
    uniqueEinsatzorte() {
      const unique = [...new Set(this.jobs.map(job => job.Einsatzort_Beschreibung).filter(Boolean))];
      return unique.sort();
    },
    uniqueEinsatzstellen() {
      const unique = [...new Set(this.jobs.map(job => job.Einsatzstelle_Beschreibung).filter(Boolean))];
      return unique.sort();
    },
    paginatedJobs() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredJobs.slice(start, end);
    },
    totalPages() {
      return Math.ceil(this.filteredJobs.length / this.itemsPerPage);
    }
  },
  methods: {
    showToast(message, type = 'info') {
      const id = this.toastIdCounter++;
      this.toasts.push({ id, message, type });
      setTimeout(() => {
        this.removeToast(id);
      }, 4000);
    },
    removeToast(id) {
      const index = this.toasts.findIndex(t => t.id === id);
      if (index > -1) {
        this.toasts.splice(index, 1);
      }
    },
    loadContracts() {
      CASP.getVertraege((status, response) => {
        if (status === 200) {
          this.contracts = response;
        } else {
          console.error('Error loading contracts:', response);
          this.showToast('Fehler beim Laden der Verträge', 'error');
        }
      });
    },
    loadJobs() {
      CASP.getInvoiceJobs(this.selectedContract, this.startDate, this.endDate, this.skipBilled, (status, response) => {
        if (status === 200) {
          this.jobs = response;
          this.selectedJobs = [];
          this.currentPage = 1; // Reset to first page when loading new jobs
          document.getElementById("mainspinner").style.display = "none";
        } else {
          this.showToast('Fehler beim Laden der Aufträge', 'error');
        }
      });
    },
    preventTextSelection(event) {
      if (event.shiftKey) {
        event.preventDefault();
      }
    },
    toggleJob(jobId, event) {
      const index = this.filteredJobs.findIndex(j => j.id === jobId);

      if (event.shiftKey && this.lastSelectedIndex !== null) {
        // Shift-click: select range
        event.preventDefault(); // Prevent text selection
        const start = Math.min(this.lastSelectedIndex, index);
        const end = Math.max(this.lastSelectedIndex, index);

        for (let i = start; i <= end; i++) {
          const id = this.filteredJobs[i].id;
          if (!this.selectedJobs.includes(id)) {
            this.selectedJobs.push(id);
          }
        }
      } else {
        // Normal click: toggle single
        const idx = this.selectedJobs.indexOf(jobId);
        if (idx > -1) {
          this.selectedJobs.splice(idx, 1);
        } else {
          this.selectedJobs.push(jobId);
        }
      }

      this.lastSelectedIndex = index;
    },
    toggleAllJobs() {
      const allPageSelected = this.paginatedJobs.every(job => this.selectedJobs.includes(job.id));
      if (allPageSelected && this.paginatedJobs.length > 0) {
        // Deselect all jobs on current page
        const pageIds = this.paginatedJobs.map(j => j.id);
        this.selectedJobs = this.selectedJobs.filter(id => !pageIds.includes(id));
      } else {
        // Select all jobs on current page
        const pageIds = this.paginatedJobs.map(j => j.id);
        const newSelections = pageIds.filter(id => !this.selectedJobs.includes(id));
        this.selectedJobs = [...this.selectedJobs, ...newSelections];
      }
    },
    selectAllJobs() {
      // Select all filtered jobs across all pages
      this.selectedJobs = this.filteredJobs.map(j => j.id);
    },
    deselectAllJobs() {
      this.selectedJobs = [];
    },
    generatePreview() {
      if (this.selectedJobs.length === 0) {
        this.showToast('Bitte wählen Sie mindestens einen Auftrag aus', 'warning');
        return;
      }

      CASP.previewInvoice(this.selectedJobs, (status, response) => {
        if (status === 200) {
          this.invoicePreview = response;
          this.showPreviewModal = true;
        } else {
          console.error('Error generating preview:', response);
          this.showToast('Fehler beim Generieren der Vorschau: ' + (response.error || response), 'error');
        }
      });
    },
    closePreviewModal() {
      this.showPreviewModal = false;
      this.creatingInvoice = false;
      this.generatingPDF = false;
      this.createdInvoiceId = null;
    },
    billJobsWithoutPDF() {
      CASP.billSelectedJobs(this.selectedJobs, (status, response) => {
        if (status = 200) {
          this.showToast('Aufträge abgerechnet', 'success');
          this.closePreviewModal();
        }
      });
    },
    createInvoiceWithPDF() {
      // Step 1: Create Invoice
      this.creatingInvoice = true;

      CASP.createInvoice(this.selectedJobs, this.invoicePreview, (status, response) => {
        if (status === 200) {
          this.creatingInvoice = false;
          this.createdInvoiceId = response.invoice.id;

          // Step 2: Generate PDF
          this.generatingPDF = true;

          CASP.generateInvoicePDF(response.invoice.id, (pdfStatus, pdfResponse) => {
            this.generatingPDF = false;

            if (pdfStatus === 200) {
              // Success - close modal and refresh
              setTimeout(() => {
                this.closePreviewModal();
                this.invoicePreview = null;
                this.selectedJobs = [];
                this.loadJobs();

                // Auto-open PDF
                CASP.getInvoicePDF(response.invoice.id);

                this.showToast('Rechnung und PDF erfolgreich erstellt!', 'success');
              }, 500);
            } else {
              console.error('Error generating PDF:', pdfResponse);
              this.showToast('Rechnung erstellt, aber Fehler beim PDF: ' + (pdfResponse.error || pdfResponse), 'error');
              this.closePreviewModal();
              this.loadJobs();
            }
          });
        } else {
          this.creatingInvoice = false;
          console.error('Error creating invoice:', response);
          this.showToast('Fehler beim Erstellen der Rechnung: ' + (response.error || response), 'error');
        }
      });
    },
    createInvoice() {
      this.creatingInvoice = true;

      CASP.createInvoice(this.selectedJobs, this.invoicePreview, (status, response) => {
        this.creatingInvoice = false;

        if (status === 200) {
          this.showToast('Rechnung erfolgreich erstellt!', 'success');
          this.invoicePreview = null;
          this.selectedJobs = [];
          this.loadJobs();

          // Generate PDF immediately
          this.generatePDF(response.invoice.id);
        } else {
          console.error('Error creating invoice:', response);
          this.showToast('Fehler beim Erstellen der Rechnung: ' + (response.error || response), 'error');
        }
      });
    },
    loadInvoices() {
      this.loadingInvoices = true;

      CASP.getInvoices((status, response) => {
        this.loadingInvoices = false;

        if (status === 200) {
          this.invoices = response;
        } else {
          console.error('Error loading invoices:', response);
          this.showToast('Fehler beim Laden der Rechnungen', 'error');
        }
      });
    },
    generatePDF(invoiceId) {
      CASP.generateInvoicePDF(invoiceId, (status, response) => {
        if (status === 200) {
          this.showToast('PDF erfolgreich generiert!', 'success');
          this.loadInvoices();
          // Auto-open PDF
          setTimeout(() => {
            this.viewPDF(invoiceId);
          }, 500);
        } else {
          console.error('Error generating PDF:', response);
          this.showToast('Fehler beim Generieren des PDFs: ' + (response.error || response), 'error');
        }
      });
    },
    viewPDF(invoiceId) {
      CASP.getInvoicePDF(invoiceId);
    },
    updateInvoiceStatus(invoiceId, newStatus) {
      CASP.updateInvoiceStatus(invoiceId, newStatus, (status, response) => {
        if (status === 200) {
          const invoice = this.invoices.find(i => i.id === invoiceId);
          if (invoice) {
            invoice.status = newStatus;
          }

          if (newStatus === 'paid') {
            this.showToast('Rechnung als bezahlt markiert. Alle zugehörigen Aufträge wurden als bezahlt markiert.', 'success');
          }
        } else {
          console.error('Error updating invoice status:', response);
          this.showToast('Fehler beim Aktualisieren des Status', 'error');
          this.loadInvoices();
        }
      });
    },
    deleteInvoiceConfirm(invoiceId) {
      if (!confirm('Möchten Sie diese Rechnung wirklich löschen? Die zugehörigen Aufträge werden wieder als "nicht abgerechnet" markiert.')) {
        return;
      }

      CASP.deleteInvoice(invoiceId, (status, response) => {
        if (status === 200) {
          this.showToast('Rechnung erfolgreich gelöscht', 'success');
          this.loadInvoices();
          if (this.selectedContract) {
            this.loadJobs();
          }
        } else {
          console.error('Error deleting invoice:', response);
          this.showToast('Fehler beim Löschen der Rechnung', 'error');
        }
      });
    },
    isNewVertragGroup(index) {
      // A contract group header row is rendered whenever the Vertrag changes
      const items = this.invoicePreview.line_items;
      return index === 0 || items[index].vertrag !== items[index - 1].vertrag;
    },
    isNewEinsatzstelleGroup(index) {
      // An Einsatzstelle group header row is rendered whenever the Vertrag or the Einsatzstelle changes
      const items = this.invoicePreview.line_items;
      return index === 0 || items[index].vertrag !== items[index - 1].vertrag
        || items[index].einsatzstelle !== items[index - 1].einsatzstelle;
    },
    formatDate(dateStr) {
      if (!dateStr) return '-';
      const date = new Date(dateStr);
      return date.toLocaleDateString('de-DE');
    },
    formatCurrency(amount) {
      return new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: 'EUR'
      }).format(amount);
    },
    initDefaultDates() {
      const now = new Date();
      // Previous month: subtract 1 from current month
      const firstDay = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const lastDay = new Date(now.getFullYear(), now.getMonth(), 0);

      // Format as YYYY-MM-DD using local time to avoid UTC offset issues
      const formatDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      this.startDate = formatDate(firstDay);
      this.endDate = formatDate(lastDay);
    }
  },
  watch: {
    activeTab(newTab) {
      if (newTab === 'list') {
        this.loadInvoices();
      }
    },
    filterEinsatzort() {
      this.currentPage = 1;
    },
    filterEinsatzstelle() {
      this.currentPage = 1;
    }
  },
  created() {

    if (localStorage) localStorage.setItem("last", '/admin/invoicing');

    this.initDefaultDates();
    this.loadContracts();
    this.loadJobs();
  }
};
</script>

<style scoped>
.hyphens-auto {
  hyphens: auto;
}

.selected-row {
  background-color: #bfdbfe !important;
  border-left: 4px solid #2563eb !important;
}

.toast-enter-active,
.toast-leave-active {
  transition: all 0.3s ease;
}

.toast-enter-from {
  opacity: 0;
  transform: translateX(100%);
}

.toast-leave-to {
  opacity: 0;
  transform: translateX(100%);
}
</style>
